import React from 'react';
import { 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  OutlinedInput, 
  Box, 
  Chip 
} from '@mui/material';
import { BILLING_TYPES, getBillingName } from '../../../constants/billingTypes';
import { BILLING_TYPE_RETURN_TYPES } from '../../../constants/selectorReturnTypes';

// Legacy export for backward compatibility
export const RETURN_VALUE_TYPES = BILLING_TYPE_RETURN_TYPES;

const BillingTypeSelector = ({ 
  multiple = false,
  value = multiple ? [] : '', 
  onChange, 
  label = "Billing Type",
  required = false,
  disabled = false,
  fullWidth = true,
  returnValue = BILLING_TYPE_RETURN_TYPES.ID, // BILLING_TYPE_RETURN_TYPES.ID or BILLING_TYPE_RETURN_TYPES.NAME
  size = 'medium',
  showEmptyOption = true,
  emptyOptionLabel = "None",
  ...props 
}) => {
  
  const billingTypeOptions = Object.entries(BILLING_TYPES).map(([key, value]) => ({
    id: key,
    name: getBillingName(value),
    value: value  // Store the actual value for reference
  }));

  // Add custom "Both" option if enabled
  const allOptions = billingTypeOptions;

  const handleChange = (event) => {
    const selectedValue = event.target.value;
    
    if (onChange) {

      if (multiple) {
        if (returnValue === BILLING_TYPE_RETURN_TYPES.ID) {
          const selectedValues = selectedValue.map(id => {
            return BILLING_TYPES[id];
          }).filter(val => val !== undefined); // Filter out undefined, but keep null
          onChange(selectedValues);
        } else {
          onChange(selectedValue);
        }
      } else {
        if (returnValue === BILLING_TYPE_RETURN_TYPES.ID) {
          const billingValue = BILLING_TYPES[selectedValue];
          onChange(billingValue); // This will be null for BOTH, which is what we want
        } else {
          onChange(selectedValue || '');
        }
      }
    }
  };

  // Find the selected billing type(s) for internal Select component usage
  const selectedValue = (() => {
    // Handle undefined values (but not null - null is valid for BOTH)
    if (value === undefined || value === '') return multiple ? [] : '';
    
    if (multiple) {
      if (returnValue === BILLING_TYPE_RETURN_TYPES.ID) {
        return value.map(billingValue => {
          // Special handling for null value (should map to BOTH)
          if (billingValue === null) {
            return 'BOTH';
          }
          // Find the key that matches the billing value
          const foundKey = Object.entries(BILLING_TYPES).find(([, val]) => val === billingValue)?.[0];
          return foundKey;
        }).filter(key => key !== undefined);
      } else {
        return Array.isArray(value) ? value : [];
      }
    } else {
      if (returnValue === BILLING_TYPE_RETURN_TYPES.ID) {
        // Special handling for null value (should map to BOTH)
        if (value === null) {
          return 'BOTH';
        }
        // Find the key that matches the billing value
        const foundKey = Object.entries(BILLING_TYPES).find(([, val]) => val === value)?.[0];
        return foundKey || '';
      } else {
        return value;
      }
    }
  })();

  const renderValue = (selected) => {
    if (multiple) {
      if (!selected || selected.length === 0) {
        return <em>{emptyOptionLabel}</em>;
      }
      return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {selected.map((value) => {

            const option = billingTypeOptions.find(opt => opt.id === value);
            return (
              <Chip 
                key={value} 
                label={option?.name} 
                size="small"
              />
            );
          })}
        </Box>
      );
    }
    
    // Handle single select rendering
    if (!selected) return '';
    const option = billingTypeOptions.find(opt => opt.id === selected);
    return option?.name || selected;
  };

  return (
    <FormControl fullWidth={fullWidth} disabled={disabled} size={size} {...props}>
      <InputLabel>{label}</InputLabel>
      <Select
        multiple={multiple}
        value={selectedValue}
        onChange={handleChange}
        label={label}
        disabled={disabled}
        input={multiple ? <OutlinedInput label={label} /> : undefined}
        renderValue={renderValue}
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 300,
              zIndex: 1350 // Ensure dropdown appears above table
            }
          }
        }}
      >
        {!required && !multiple && showEmptyOption && (
          <MenuItem value="">
            <em>{emptyOptionLabel}</em>
          </MenuItem>
        )}
        {allOptions.map(option => (
          <MenuItem 
            key={option.id} 
            value={option.id}
            sx={option.isCustom ? {
              backgroundColor: '#F3F4F6',
              fontWeight: 'bold',
              borderBottom: '1px solid #E5E7EB',
              '&:hover': {
                backgroundColor: '#E5E7EB'
              },
              '&.Mui-selected': {
                backgroundColor: '#D1FAE5',
                '&:hover': {
                  backgroundColor: '#A7F3D0'
                }
              }
            } : {}}
          >
            {option.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default BillingTypeSelector;